import { FC, useCallback, useMemo, useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { COLORS, FONTS } from '../../../constants';
import BasicBookingInfo from '../../../components/BasicBookingInfor/BasicBookingInfor';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { ICartItem } from '../../../types/camp.types';
import dayjs from 'dayjs';
import { convertPrice } from '../../../services/utils';
import { NavigationService } from '../../../services/navigation';
import { useDispatch } from 'react-redux';
import {
  deleteCartActivityItem,
  deleteCartDishItem,
} from '../../../services/redux/reducers/CartSlice';
import { getDatesBetween } from '../../../services/utils/dateConvert';
import { DISH_TYPES } from '../../../constants/dishs';
import { TabelDeleteableItem } from '../../BookingDetail/components/Table/TabelDeleteableItem';
import { IExtraDish } from '../../../types/dishes.types';
import { IExtraActivity } from '../../../types/activities.types';

interface ITableItem {
  product: string;
  desc: string;
  price: string;
}

interface CardItemProps {
  cartItem: ICartItem;
  onRemove: () => void;
}

const CartCollapseItem: FC<CardItemProps> = ({ cartItem, onRemove }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const betweenDates = getDatesBetween(
    cartItem.inputData.start_date,
    cartItem.inputData.end_date,
    cartItem.camp.room_availability
  );

  const showPrice = (price: string | number) => {
    if (price) {
      return convertPrice(Number(price));
    } else {
      return 'Free';
    }
  };

  const handleRedirect = () => {
    NavigationService.push('Root', {
      screen: 'MenuTab',
      params: {
        screen: 'BookingDetail',
        params: {
          id: cartItem.camp.id,
          cart_id: cartItem.id,
        },
      },
    });
  };

  const handleDeleteDishItem = useCallback(
    (dish: IExtraDish) => {
      dispatch(
        deleteCartDishItem({
          cartId: cartItem.id,
          deleteDish: dish,
        })
      );
    },
    [dispatch, cartItem]
  );

  const handleDeleteActivityItem = useCallback(
    (activity: IExtraActivity) => {
      dispatch(
        deleteCartActivityItem({
          cartId: cartItem.id,
          deleteActivity: activity,
        })
      );
    },
    [dispatch, cartItem]
  );

  const renderTableItemRow = useCallback(
    (item: ITableItem) => {
      return (
        <View
          style={{
            flexDirection: 'row',
            marginBottom: 15,
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              width: '74%',
              flexDirection: isDesktop() ? 'row' : 'column',
            }}
          >
            <View style={{ width: isDesktop() ? '40%' : '100%' }}>
              <Text style={styles.tableContentText}>{item.product}</Text>
            </View>
            <View style={{ width: isDesktop() ? '60%' : '100%' }}>
              <Text style={[styles.tableContentText]}>{item.desc}</Text>
            </View>
          </View>
          <View style={{ width: '25%' }}>
            <Text style={styles.tableContentText}>{item.price}</Text>
          </View>
        </View>
      );
    },
    [isDesktop]
  );

  const renderProductHeader = useMemo(() => {
    return (
      <View
        style={{
          flexDirection: 'row',
          borderBottomColor: COLORS.DIVIDER_GREY,
          borderBottomWidth: 1,
          paddingBottom: 10,
          paddingHorizontal: 20,
          paddingTop: 25,
        }}
      >
        <View style={{ width: isDesktop() ? '30%' : '75%' }}>
          <Text style={styles.tableHeaderText}>Product</Text>
        </View>
        {isDesktop() && (
          <View style={{ width: '45%' }}>
            <Text style={styles.tableHeaderText}>Description</Text>
          </View>
        )}

        <View
          style={[
            {
              width: '25%',
              flexDirection: 'row',
              alignItems: 'center',
            },
          ]}
        >
          <Text style={styles.tableHeaderText}>Price</Text>
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontSize: 12,
              fontWeight: '500',
              marginLeft: 3,
              color: COLORS.GREY,
            }}
          >
            {'(HKD)'}
          </Text>
        </View>
      </View>
    );
  }, [isDesktop]);

  const renderRooms = useMemo(() => {
    return (
      cartItem.inputData.rooms.length > 0 && (
        <View
          style={{
            paddingHorizontal: 20,
            marginBottom: 10,
          }}
        >
          <View style={{ marginBottom: 8 }}>
            <Text style={styles.tableHeaderText}>Rooms</Text>
          </View>
          <View>
            {cartItem.inputData.rooms.map((room) => {
              const roomFind =
                cartItem.inputData.price_data?.rooms.find(
                  (item) => item.id === room.room_id
                ) ||
                cartItem.camp.rooms.find((item) => item.id === room.room_id);

              if (roomFind) {
                return (
                  <>
                    {renderTableItemRow({
                      product: roomFind.name.en,
                      desc: `No. Of Room: ${room.quantity}${
                        room.is_additional_bed ? '\nAddtional bed' : ''
                      }`,
                      price: roomFind.total
                        ? `${showPrice(roomFind.total / 100)}`
                        : '',
                    })}
                  </>
                );
              }
            })}
          </View>
        </View>
      )
    );
  }, [renderTableItemRow, cartItem]);

  const renderMeals = useMemo(() => {
    return (
      <View style={{ paddingHorizontal: 20, marginBottom: 10 }}>
        <View style={{ marginBottom: 8 }}>
          <Text style={styles.tableHeaderText}>Meals</Text>
        </View>
        <View>
          {cartItem.camp.included_meal.breakfast &&
            renderTableItemRow({
              product: 'Breakfast',
              desc: cartItem.camp.included_meal.breakfast.name,
              price: showPrice(cartItem.camp.included_meal.breakfast.price),
            })}
          {cartItem.camp.included_meal.lunch &&
            renderTableItemRow({
              product: 'Lunch',
              desc: cartItem.camp.included_meal.lunch.name,
              price: showPrice(cartItem.camp.included_meal.lunch.price),
            })}
          {cartItem.inputData.dinner_meal &&
            renderTableItemRow({
              product: 'Dinner',
              desc: cartItem.inputData.dinner_meal.name,
              price: showPrice(cartItem.inputData.dinner_meal.price),
            })}
        </View>
      </View>
    );
  }, [renderTableItemRow, cartItem]);

  const renderExtraDishes = useMemo(() => {
    return (
      cartItem.inputData.extra_dishes.length > 0 && (
        <View
          style={{
            backgroundColor: COLORS.WHITE,
            paddingHorizontal: 20,
            paddingTop: 10,
            paddingBottom: 5,
            borderRadius: 18,
            borderWidth: 1,
            borderColor: COLORS.LGREY,
            marginBottom: 10,
          }}
        >
          <View style={{ marginBottom: 8 }}>
            <Text style={styles.tableHeaderText}>Additional Meals</Text>
          </View>
          {betweenDates.map((date, index) => {
            const dateDishes = cartItem.inputData.extra_dishes.filter(
              (dish) => dish.date === date
            );
            const breakfastDateDishes = dateDishes.filter(
              (dish) => dish.type === DISH_TYPES.BREAKFAST
            );
            const lunchDateDishes = dateDishes.filter(
              (dish) => dish.type === DISH_TYPES.LUNCH
            );
            const dinnerDateDishes = dateDishes.filter(
              (dish) => dish.type === DISH_TYPES.DINNER
            );

            return dateDishes.length > 0 ? (
              <View style={{ marginBottom: 10 }}>
                <Text style={styles.tableSubHeaderText}>
                  {`Day ${index + 1} - ${dayjs(new Date(date)).format(
                    'DD/MM/YYYY'
                  )}`}
                </Text>
                {/* Breakfast */}
                {breakfastDateDishes.length > 0 && (
                  <View>
                    <Text
                      style={[styles.tableContentText, { marginBottom: 5 }]}
                    >
                      Breakfast
                    </Text>
                    {breakfastDateDishes.map((item) => {
                      return (
                        <TabelDeleteableItem
                          name={item.dish.name}
                          description={`No. Of Meal: ${item.quantity}`}
                          price={convertPrice(item.dish.price * item.quantity)}
                          onDeleteItem={() => {
                            handleDeleteDishItem(item);
                          }}
                          isCanDelete={true}
                        />
                      );
                    })}
                  </View>
                )}
                {/* Lunch */}
                {lunchDateDishes.length > 0 && (
                  <View>
                    <Text
                      style={[styles.tableContentText, { marginBottom: 5 }]}
                    >
                      Lunch
                    </Text>
                    {lunchDateDishes.map((item) => {
                      return (
                        <TabelDeleteableItem
                          name={item.dish.name}
                          description={`No. Of Meal: ${item.quantity}`}
                          price={convertPrice(item.dish.price * item.quantity)}
                          onDeleteItem={() => {
                            handleDeleteDishItem(item);
                          }}
                          isCanDelete={true}
                        />
                      );
                    })}
                  </View>
                )}
                {/* Dinner */}
                {dinnerDateDishes.length > 0 && (
                  <View>
                    <Text
                      style={[styles.tableContentText, { marginBottom: 5 }]}
                    >
                      Dinner
                    </Text>
                    {dinnerDateDishes.map((item) => {
                      return (
                        <TabelDeleteableItem
                          name={item.dish.name}
                          description={`No. Of Meal: ${item.quantity}`}
                          price={convertPrice(item.dish.price * item.quantity)}
                          onDeleteItem={() => {
                            handleDeleteDishItem(item);
                          }}
                          isCanDelete={true}
                        />
                      );
                    })}
                  </View>
                )}
              </View>
            ) : (
              <></>
            );
          })}
        </View>
      )
    );
  }, [cartItem.inputData.extra_dishes, betweenDates, handleDeleteDishItem]);

  const renderExtraActivities = useMemo(() => {
    return (
      cartItem.inputData.extra_activities.length > 0 && (
        <View
          style={{
            backgroundColor: COLORS.WHITE,
            paddingHorizontal: 20,
            paddingTop: 10,
            paddingBottom: 5,
            borderRadius: 18,
            borderWidth: 1,
            borderColor: COLORS.LGREY,
            marginBottom: 10,
          }}
        >
          <View style={{ marginBottom: 8 }}>
            <Text style={styles.tableHeaderText}>Additional Activities</Text>
          </View>
          {betweenDates.map((date, index) => {
            const dateActivities = cartItem.inputData.extra_activities.filter(
              (activity) => activity.date === date
            );

            return dateActivities.length > 0 ? (
              <View style={{ marginBottom: 10 }}>
                <Text style={styles.tableSubHeaderText}>
                  {`Day ${index + 1} - ${dayjs(new Date(date)).format(
                    'DD/MM/YYYY'
                  )}`}
                </Text>
                {dateActivities.length > 0 && (
                  <View>
                    {dateActivities.map((item) => {
                      return (
                        <TabelDeleteableItem
                          name={item.activity.name}
                          description={`${dayjs(
                            new Date(item.activity.start_time)
                          ).format('hh:mm a')} - ${dayjs(
                            new Date(item.activity.end_time)
                          ).format('hh:mm a')}`}
                          price={convertPrice(
                            item.activity.price * item.attendees
                          )}
                          onDeleteItem={() => {
                            handleDeleteActivityItem(item);
                          }}
                          isCanDelete={true}
                        />
                      );
                    })}
                  </View>
                )}
              </View>
            ) : (
              <></>
            );
          })}
        </View>
      )
    );
  }, [
    cartItem.inputData.extra_activities,
    betweenDates,
    handleDeleteActivityItem,
  ]);

  const renderActivities = useMemo(() => {
    return (
      <View style={{ paddingHorizontal: 20 }}>
        <View style={{ marginBottom: 8 }}>
          <Text style={styles.tableHeaderText}>Included Activities</Text>
        </View>
        <View>
          {cartItem.camp.activities.map((item) => {
            return renderTableItemRow({
              product: item.name,
              desc: `${dayjs(new Date(cartItem.inputData.start_date)).format(
                'DD.MM.YYYY'
              )}\n${dayjs(new Date(item.start_time)).format(
                'hh:mm a'
              )} - ${dayjs(new Date(item.end_time)).format(
                'hh:mm a'
              )}\nVenue: ${item.venue}`,
              price: showPrice(item.price),
            });
          })}
        </View>
      </View>
    );
  }, [renderTableItemRow, cartItem]);

  return (
    <View
      key={cartItem.id}
      style={[
        {
          flexDirection: 'column',
          backgroundColor: COLORS.LIGHT_RED,
          borderRadius: 18,
          padding: 10,
          position: 'relative',
          marginBottom: 15,
        },
      ]}
    >
      <TouchableOpacity
        onPress={onRemove}
        style={{ position: 'absolute', right: -5, top: -5 }}
      >
        <Image
          style={{
            width: 29,
            height: 29,
          }}
          source={require('../../../assets/close-circle-icon.png')}
        />
      </TouchableOpacity>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'center',
          paddingBottom: 15,
        }}
      >
        <TouchableOpacity
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: COLORS.RED_PRIMARY,
            paddingHorizontal: 14,
            paddingVertical: 5,
            borderRadius: 13,
          }}
          onPress={() => {
            setIsOpen(!isOpen);
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.AvenirMedium,
              fontWeight: '500',
              fontSize: 16,
              color: COLORS.WHITE,
              marginRight: 5,
            }}
          >
            {isOpen ? 'Collapse' : 'Detail'}
          </Text>
          <Image
            source={
              isOpen
                ? require('../../../assets/up-arrow.png')
                : require('../../../assets/down-arrow.png')
            }
            style={{
              width: 18,
              height: 10,
              tintColor: COLORS.WHITE,
            }}
          />
        </TouchableOpacity>
      </View>
      <View
        style={{
          flexDirection: 'row',
          marginBottom: 15,
          justifyContent: 'space-between',
        }}
      >
        <View
          style={{
            width: '33%',
          }}
        >
          <TouchableOpacity onPress={handleRedirect}>
            <Text style={[styles.contentText, { textTransform: 'uppercase' }]}>
              {cartItem.camp.name}
            </Text>
          </TouchableOpacity>
        </View>
        <View
          style={{
            width: '33%',
          }}
        >
          <TouchableOpacity onPress={handleRedirect}>
            <Text style={styles.contentText}>
              {dayjs(new Date(cartItem.inputData.created_at)).format(
                'DD MMM YYYY'
              )}
            </Text>
          </TouchableOpacity>
        </View>
        <View
          style={{
            width: '33%',
          }}
        >
          {cartItem.inputData.price_data && (
            <TouchableOpacity onPress={handleRedirect}>
              <Text style={styles.contentText}>{`${showPrice(
                cartItem.inputData.price_data.total / 100
              )} HKD`}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
      {isOpen && (
        <View>
          <View
            style={{
              borderTopColor: COLORS.DIVIDER_GREY,
              borderTopWidth: 1,
              paddingHorizontal: 20,
              paddingTop: 10,
              paddingBottom: 20,
              borderBottomColor: COLORS.DIVIDER_GREY,
              borderBottomWidth: 1,
            }}
          >
            <BasicBookingInfo data={cartItem.inputData} camp={cartItem.camp} />
          </View>
          <View
            style={{
              paddingTop: 10,
              paddingBottom: 20,
              borderBottomColor: COLORS.DIVIDER_GREY,
              borderBottomWidth: 1,
            }}
          >
            {renderProductHeader}
            <View
              style={{
                paddingTop: 20,
              }}
            >
              {renderRooms}
              {renderMeals}
              {renderExtraDishes}
              {renderActivities}
              {renderExtraActivities}
            </View>
          </View>
          <View
            style={{
              paddingHorizontal: 20,
              paddingTop: 10,
              paddingBottom: 5,
            }}
          >
            <View>
              <View style={{ flexDirection: 'row', marginBottom: 15 }}>
                <View style={{ width: '30%' }}>
                  <Text
                    style={[styles.tableContentText, { color: COLORS.BLACK }]}
                  >
                    {'Total Price:'}
                  </Text>
                </View>
                <View style={{ width: '45%' }}>
                  <Text
                    style={[styles.tableContentText, { color: COLORS.BLACK }]}
                  >
                    {cartItem.inputData.price_data?.price_type ===
                      'subsidized' && 'Subsidised booking rate applied'}
                  </Text>
                </View>
                <View style={{ width: '25%' }}>
                  <Text
                    style={[styles.tableContentText, { color: COLORS.BLACK }]}
                  >
                    {cartItem.inputData.price_data
                      ? showPrice(cartItem.inputData.price_data.total / 100)
                      : ''}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

export default CartCollapseItem;

const styles = StyleSheet.create({
  contentText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 16,
    color: COLORS.BLACK,
    textAlign: 'center',
  },
  tableHeaderText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '800',
    fontSize: 19,
    color: COLORS.BLACK,
  },
  tableSubHeaderText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 17,
    marginBottom: 7,
    color: COLORS.DARKER_GREY,
  },
  tableContentText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    fontSize: 17,
    color: COLORS.GREY,
    paddingRight: 5,
  },
});
